import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Phone, Mail, ChevronRight,
  Sparkles, Award, Globe, Users, Briefcase, Building2,
  CheckCircle2, Target, Heart, TrendingUp
} from 'lucide-react';
import '../styles/AboutUs.css';

const AboutUs = () => {
  const navigate = useNavigate();

  const scrollTop = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  // Reveal on scroll
  useEffect(() => {
    const elements = document.querySelectorAll('.au-reveal');
    if (!('IntersectionObserver' in window)) {
      elements.forEach((el) => el.classList.add('au-in-view'));
      return;
    }
    elements.forEach((el) => el.classList.add('au-anim-ready'));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('au-in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );

    elements.forEach((el) => observer.observe(el));

    const safety = setTimeout(() => {
      document.querySelectorAll('.au-reveal:not(.au-in-view)').forEach((el) => {
        el.classList.add('au-in-view');
      });
    }, 2000);

    return () => {
      elements.forEach((el) => observer.unobserve(el));
      clearTimeout(safety);
    };
  }, []);

  const departments = [
    'Front Office', 'F&B Production', 'F&B Service',
    'Housekeeping', 'Spa & Wellness', 'Laundry',
    'Operations', 'Human Resources', 'Engineering'
  ];

  const values = [
    {
      icon: Award,
      title: 'Excellence First',
      desc: 'We uphold the highest standards in every placement and partnership we make.'
    },
    {
      icon: Heart,
      title: 'People-Centric',
      desc: 'Every candidate and employer is treated with the discretion and respect they deserve.'
    },
    {
      icon: Target,
      title: 'Precision Matching',
      desc: 'We connect the right talent with the right opportunity — never just any opportunity.'
    },
    {
      icon: TrendingUp,
      title: 'Growth Focused',
      desc: 'We empower careers and businesses to scale within the hospitality industry.'
    }
  ];

  return (
    <div className="au-page">
      {/* ═══════ HERO SECTION ═══════ */}
      <section className="au-hero">
        <div className="au-hero-bg" />
        <div className="au-hero-overlay" />
        <div className="au-hero-vignette" />
        <div className="au-hero-mesh au-mesh-1" />
        <div className="au-hero-mesh au-mesh-2" />

        <div className="au-hero-inner">
          {/* Back to home */}
          <button className="au-back-btn" onClick={() => scrollTop('/')}>
            <ArrowLeft size={14} />
            <span>Back to Home</span>
          </button>

          {/* Breadcrumb */}
          <div className="au-breadcrumb au-fade-up">
            <button onClick={() => scrollTop('/')}>Home</button>
            <ChevronRight size={12} />
            <span>About Us</span>
          </div>

          {/* Hero content */}
          <div className="au-hero-content">
            <div className="au-eyebrow au-fade-up au-delay-1">
              <Sparkles size={12} />
              <span>Our Story</span>
            </div>

            <h1 className="au-hero-title au-fade-up au-delay-2">
              Redefining <br />
              <span className="au-gold-text">Hospitality Recruitment</span>
            </h1>

            <p className="au-hero-sub au-fade-up au-delay-3">
              A curated network connecting elite hospitality talent with the world's
              most distinguished establishments. Built in Dubai, trusted globally.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════ INTRO SECTION ═══════ */}
      <section className="au-section au-intro-section au-reveal">
        <div className="au-section-inner">
          <div className="au-intro-grid">
            <div className="au-intro-left au-reveal-left">
              <span className="au-section-eyebrow">Welcome</span>
              <h2 className="au-section-heading">
                The home of <em>premium</em> hospitality careers.
              </h2>
            </div>

            <div className="au-intro-right au-reveal-right">
              <p className="au-lead-text">
                Welcome to the online home of <strong>HotelPlacement.ae</strong> —
                the very best in hospitality jobs and recruitment services.
              </p>
              <p>
                We are dedicated to offering <strong>the finest hospitality
                management opportunities</strong> across Dubai, the UAE, and beyond.
                Our platform was founded with one purpose: to give hotels, caterers,
                restaurants, resorts, pubs, bars, and eateries the most refined
                recruitment experience possible.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ WHAT WE ARE ═══════ */}
      <section className="au-section au-what-section au-reveal">
        <div className="au-section-inner">
          <div className="au-section-header">
            <span className="au-section-eyebrow">What We Are</span>
            <h2 className="au-section-heading-center">
              More than a job board — <em>a complete ecosystem.</em>
            </h2>
          </div>

          <div className="au-features-grid">
            <div className="au-feature-card">
              <div className="au-feature-icon"><Briefcase size={22} /></div>
              <h3>Job Board</h3>
              <p>Curated listings from premium hospitality establishments worldwide.</p>
            </div>
            <div className="au-feature-card">
              <div className="au-feature-icon"><Users size={22} /></div>
              <h3>Recruiting Hub</h3>
              <p>A dedicated space where employers and recruiters discover top talent.</p>
            </div>
            <div className="au-feature-card">
              <div className="au-feature-icon"><Target size={22} /></div>
              <h3>Applicant Tracking</h3>
              <p>Sophisticated tools to manage the entire hiring pipeline seamlessly.</p>
            </div>
            <div className="au-feature-card">
              <div className="au-feature-icon"><Award size={22} /></div>
              <h3>Assessment Tool</h3>
              <p>Evaluate candidates with industry-specific assessments and insights.</p>
            </div>
            <div className="au-feature-card">
              <div className="au-feature-icon"><Heart size={22} /></div>
              <h3>Talent Community</h3>
              <p>A thriving network of verified hospitality professionals.</p>
            </div>
            <div className="au-feature-card">
              <div className="au-feature-icon"><TrendingUp size={22} /></div>
              <h3>Onboarding Platform</h3>
              <p>End-to-end support from offer letter to first day on the job.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ DEPARTMENTS ═══════ */}
      <section className="au-section au-depts-section au-reveal">
        <div className="au-section-inner">
          <div className="au-depts-grid">
            <div className="au-depts-text au-reveal-left">
              <span className="au-section-eyebrow">Industry Coverage</span>
              <h2 className="au-section-heading">
                Every role, <em>every department.</em>
              </h2>
              <p className="au-section-desc">
                From front office to engineering, our platform serves professionals
                across every facet of the hospitality industry. Hotel school students,
                seasoned managers, and everyone in between — find your place here.
              </p>
            </div>

            <div className="au-depts-list au-reveal-right">
              {departments.map((dept, i) => (
                <div
                  key={dept}
                  className="au-dept-tag"
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <CheckCircle2 size={14} className="au-gold-icon" />
                  <span>{dept}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ VALUES ═══════ */}
      <section className="au-section au-values-section au-reveal">
        <div className="au-section-inner">
          <div className="au-section-header">
            <span className="au-section-eyebrow">Our Commitments</span>
            <h2 className="au-section-heading-center">
              Built on four <em>uncompromising principles.</em>
            </h2>
          </div>

          <div className="au-values-grid">
            {values.map((v, i) => (
              <div
                key={v.title}
                className="au-value-card"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="au-value-icon"><v.icon size={24} /></div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
                <div className="au-value-line" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ CTA BAND ═══════ */}
      <section className="au-cta au-reveal">
        <div className="au-cta-particles" />
        <div className="au-cta-inner">
          <span className="au-section-eyebrow au-eyebrow-light">Begin Your Journey</span>
          <h2 className="au-cta-heading">
            Ready to experience <em>hospitality recruitment</em><br />
            the way it should be?
          </h2>
          <p className="au-cta-text">
            Whether you're a candidate seeking your next opportunity or an employer
            in search of exceptional talent — your journey begins here.
          </p>
          <button className="au-cta-btn" onClick={() => scrollTop('/')}>
            <span>Explore the Platform</span>
            <ChevronRight size={16} />
          </button>
        </div>
      </section>

      {/* ═══════ FOOTER ═══════ */}
      <footer className="au-footer">
        <div className="au-footer-content">
          <div className="au-footer-brand">
            <h3>HotelPlacement.ai</h3>
            <p>Redefining hospitality recruitment.</p>
            <div className="au-footer-socials">
              <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer">LinkedIn</a>
              <span className="au-footer-dot">·</span>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer">Twitter</a>
              <span className="au-footer-dot">·</span>
              <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer">Facebook</a>
            </div>
          </div>

          <div className="au-footer-col">
            <h4>Contact</h4>
            <div className="au-footer-item">
              <MapPin size={14} />
              <span>Aspin Towers, Sheikh Zayed Road, Dubai</span>
            </div>
            <div className="au-footer-item">
              <Phone size={14} />
              <span>+971 4250 3121</span>
            </div>
            <div className="au-footer-item">
              <Mail size={14} />
              <span>info@hotelplacement.ae</span>
            </div>
          </div>

          <div className="au-footer-col">
            <h4>Company</h4>
            <button onClick={() => scrollTop('/')}>Home</button>
            <button className="au-footer-active">About Us</button>
            <button onClick={() => scrollTop('/faq')}>FAQs</button>
            <button onClick={() => scrollTop('/privacy-policy')}>Privacy Policy</button>
            <button onClick={() => scrollTop('/terms-conditions')}>Terms & Conditions</button>
          </div>
        </div>

        <div className="au-footer-bottom">
          © 2026 HotelPlacement.ai · All rights reserved
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;